import React, { useContext } from "react";
import { Box, Container, Grid, Typography, IconButton } from "@mui/material";
import { Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../Theme/ThemeContext";

export default function HomeFooter(props) {
  const { isDark } = useContext(ThemeContext);
  const settings = useSelector(state => state.settingsdata.settings);
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box component="footer" sx={{
      bgcolor: isDark ? '#08080A' : '#F8FAFC', // Dynamic background
      color: isDark ? 'white' : '#120A2A',
      pt: 10,
      pb: 4,
      position: 'relative',
      overflow: 'hidden',
      borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.06)',
      transition: 'background-color 0.3s ease, border-color 0.3s ease'
    }}>
      {/* Background radial gradient */}
      <Box sx={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '500px',
        height: '500px',
        background: isDark ? 'radial-gradient(circle, rgba(233, 174, 49, 0.1) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(233, 174, 49, 0.05) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        zIndex: 0,
        transition: 'background 0.3s ease'
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={8}>

          {/* Column 1: Brand & About */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 3 }}>
              <img src={isDark ? require("../../assets/img/logo138x75white.png").default : require("../../assets/img/logo138x75black.png").default} alt="Logo" style={{ height: '42px', objectFit: 'contain', marginBottom: '20px' }} />
              <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#475569', lineHeight: 1.8, mb: 3, transition: 'color 0.3s ease' }}>
                {t('about_us_content2')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {settings && settings.InstagramHandle && (
                  <IconButton href={settings.InstagramHandle} target="_blank" sx={{ color: isDark ? 'white' : '#120A2A', bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: '#E9AE31', color: '#0A0A0B' } }}>
                    <Instagram size={20} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontFamily: 'Outfit, sans-serif', color: '#E9AE31' }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography
                variant="body2"
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', color: isDark ? '#94a3b8' : '#475569', '&:hover': { color: '#E9AE31', transform: 'translateX(5px)' }, transition: 'all 0.3s' }}
              >
                {t('home')}
              </Typography>
              <Typography
                variant="body2"
                onClick={() => navigate('/bookings')}
                sx={{ cursor: 'pointer', color: isDark ? '#94a3b8' : '#475569', '&:hover': { color: '#E9AE31', transform: 'translateX(5px)' }, transition: 'all 0.3s' }}
              >
                {t('myaccount')}
              </Typography>
              <Typography
                variant="body2"
                onClick={() => navigate('/about-us')}
                sx={{ cursor: 'pointer', color: isDark ? '#94a3b8' : '#475569', '&:hover': { color: '#E9AE31', transform: 'translateX(5px)' }, transition: 'all 0.3s' }}
              >
                {t('about_us')}
              </Typography>
              <Typography
                variant="body2"
                onClick={() => navigate('/contact-us')}
                sx={{ cursor: 'pointer', color: isDark ? '#94a3b8' : '#475569', '&:hover': { color: '#E9AE31', transform: 'translateX(5px)' }, transition: 'all 0.3s' }}
              >
                {t('contact_us')}
              </Typography>
            </Box>
          </Grid>

          {/* Column 3: Contact Info */}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontFamily: 'Outfit, sans-serif', color: '#E9AE31' }}>
              Contact Us
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Mail size={20} color="#E9AE31" />
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#475569', transition: 'color 0.3s ease' }}>
                  {settings.CompanyEmail || 'info@inspire.qa'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Phone size={20} color="#E9AE31" />
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#475569', transition: 'color 0.3s ease' }}>
                  {settings.CompanyPhone || '+1 234 567 890'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <MapPin size={20} color="#E9AE31" />
                <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#475569', transition: 'color 0.3s ease' }}>
                  {settings.CompanyAddress || '123 Innovation Dr, Tech City'}
                </Typography>
              </Box>
            </Box>
          </Grid>

        </Grid>

        <Box sx={{ 
          borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)', 
          mt: 8, 
          pt: 4, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center', 
          gap: 1.5, 
          transition: 'border-color 0.3s ease' 
        }}>
          <Typography variant="body2" sx={{ color: isDark ? '#64748b' : '#94a3b8', textAlign: 'center', transition: 'color 0.3s ease' }}>
            &copy; {new Date().getFullYear()} {settings.CompanyName}. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
            <Typography variant="body2" onClick={() => navigate('/privacy-policy')} sx={{ cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: isDark ? 'white' : '#120A2A' }, transition: 'color 0.3s ease' }}>
              {t('privacy_policy')}
            </Typography>
            <Typography variant="body2" onClick={() => navigate('/term-condition')} sx={{ cursor: 'pointer', color: isDark ? '#64748b' : '#94a3b8', '&:hover': { color: isDark ? 'white' : '#120A2A' }, transition: 'color 0.3s ease' }}>
              {t('term_condition')}
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
